import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Dialog } from '@mui/material';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, colors, DatePickerInput, Dropdown, Input, Typography } from '@leapeasy/ui-kit';
import { UserTable } from 'components/Tables/UserTable';
import { getUserTableConfigForRoute } from 'components/Tables/UserTable/userTableConfig';
import {
  deleteUsersAction,
  getUserBuildingSearchAction,
  setUserBuildingSearchAction,
} from 'store/actions/userActions';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';
import Img_1 from 'assets/img_1.png';
import { GlobalUXState } from 'components/GlobalUXState';
import {
  preloadUserDelegationContext,
  resolveUserDelegationRoute,
} from 'utils/userDelegationNavigation';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildUserByRoleListFilterChips,
  parseUserByRoleListFilterChipId,
} from 'utils/listTableActiveFilterChips';
import {
  buildUserBuildingSearchFilterOptions,
  filterUserBuildingSearchUsers,
  paginateUserBuildingSearchUsers,
  sortUserBuildingSearchUsers,
} from 'utils/userBuildingSearchDataSource';

const TREEVIEW_SEARCH_TAB = 'search';

const DEFAULT_TABLE_FILTER = {
  options: ['accepter.username', 'accepter_email', 'accepter.job_title'],
  searchText: '',
  searchPlaceholder: 'Search User...',
};

const DEFAULT_EXTRA_FILTERS = {
  selectedBuilings: [],
  selectedJobTitle: [],
  selectedInvitedDate: '',
};

const parseUserByRoleFilters = (searchParams, _paramKey, fallback) => ({
  selectedBuilings:
    searchParams.get('propertyCount') || searchParams.get('buildings')
      ? (searchParams.get('propertyCount') || searchParams.get('buildings'))
          .split(',')
          .filter(Boolean)
      : fallback.selectedBuilings,
  selectedJobTitle: searchParams.get('jobTitle')
    ? searchParams.get('jobTitle').split(',').filter(Boolean)
    : fallback.selectedJobTitle,
  selectedInvitedDate: searchParams.get('invitedDate') || fallback.selectedInvitedDate || '',
});

const serializeUserByRoleFilters = (filters) => ({
  propertyCount:
    Array.isArray(filters.selectedBuilings) && filters.selectedBuilings.length > 0
      ? filters.selectedBuilings.join(',')
      : '',
  jobTitle:
    Array.isArray(filters.selectedJobTitle) && filters.selectedJobTitle.length > 0
      ? filters.selectedJobTitle.join(',')
      : '',
  invitedDate: filters.selectedInvitedDate || '',
});

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const UserBuildingSearchTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const usersState = useSelector((state) => state.getIn(['user', 'user_building_search']));
  const qParam = searchParams.get('q') || '';

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: DEFAULT_TABLE_FILTER,
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_EXTRA_FILTERS,
    parseExtraFilters: parseUserByRoleFilters,
    serializeExtraFilters: serializeUserByRoleFilters,
  });

  const [apiSearchText, setApiSearchText] = useState(qParam);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBuilings, setSelectedBuildings] = useState([]);
  const [selectedJobTitle, setSelectedJobTitle] = useState([]);
  const [selectedInvitedDate, setSelectedInvitedDate] = useState('');
  const [selectedBuilingsDraft, setSelectedBuildingsDraft] = useState([]);
  const [selectedJobTitleDraft, setSelectedJobTitleDraft] = useState([]);
  const [selectedInvitedDateDraft, setSelectedInvitedDateDraft] = useState('');
  const lastFetchedQRef = useRef('');

  const searchActive = Boolean(qParam.trim());

  useEffect(() => {
    setApiSearchText(qParam);
  }, [qParam]);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedBuildings = Array.isArray(extraFilters.selectedBuilings)
      ? extraFilters.selectedBuilings
      : [];
    const nextSelectedJobTitle = Array.isArray(extraFilters.selectedJobTitle)
      ? extraFilters.selectedJobTitle
      : [];
    const nextSelectedInvitedDate = extraFilters.selectedInvitedDate || '';
    setSelectedBuildings((prev) =>
      areArraysEqual(prev, nextSelectedBuildings) ? prev : nextSelectedBuildings,
    );
    setSelectedJobTitle((prev) =>
      areArraysEqual(prev, nextSelectedJobTitle) ? prev : nextSelectedJobTitle,
    );
    setSelectedInvitedDate((prev) =>
      prev === nextSelectedInvitedDate ? prev : nextSelectedInvitedDate,
    );
    setSelectedBuildingsDraft((prev) =>
      areArraysEqual(prev, nextSelectedBuildings) ? prev : nextSelectedBuildings,
    );
    setSelectedJobTitleDraft((prev) =>
      areArraysEqual(prev, nextSelectedJobTitle) ? prev : nextSelectedJobTitle,
    );
    setSelectedInvitedDateDraft((prev) =>
      prev === nextSelectedInvitedDate ? prev : nextSelectedInvitedDate,
    );
  }, [extraFilters]);

  useEffect(() => {
    const trimmed = qParam.trim();
    if (!trimmed) {
      lastFetchedQRef.current = '';
      setSearchLoading(false);
      dispatch(setUserBuildingSearchAction([]));
      return;
    }
    if (lastFetchedQRef.current === trimmed) return;
    lastFetchedQRef.current = trimmed;
    setSearchLoading(true);
    dispatch(getUserBuildingSearchAction({ text: trimmed }));
  }, [dispatch, qParam]);

  useEffect(() => {
    if (searchLoading) {
      setSearchLoading(false);
    }
  }, [usersState, searchLoading]);

  const usersList = useMemo(() => {
    if (!usersState) return [];
    const rows = usersState.toJS?.() ?? usersState;
    return Array.isArray(rows) ? rows : [];
  }, [usersState]);

  const { numOfBuildings, listOfJobTitle } = useMemo(
    () => buildUserBuildingSearchFilterOptions(usersList),
    [usersList],
  );

  const filteredUsers = useMemo(
    () =>
      sortUserBuildingSearchUsers(
        filterUserBuildingSearchUsers(usersList, {
          searchText: filter.searchText,
          selectedBuilings,
          selectedJobTitle,
          selectedInvitedDate,
        }),
        sortOptions,
      ),
    [
      usersList,
      filter.searchText,
      selectedBuilings,
      selectedJobTitle,
      selectedInvitedDate,
      sortOptions,
    ],
  );

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: filteredUsers.length,
    isLoading: searchLoading,
  });

  const pagedUsers = useMemo(
    () =>
      paginateUserBuildingSearchUsers(filteredUsers, pagination.pageNumber, pagination.rowsPerPage),
    [filteredUsers, pagination.pageNumber, pagination.rowsPerPage],
  );

  const resetTableQueryState = useCallback(() => {
    setFilter(DEFAULT_TABLE_FILTER);
    setExtraFilters(DEFAULT_EXTRA_FILTERS);
    setSortOptions({});
    setSelectedBuildings([]);
    setSelectedJobTitle([]);
    setSelectedInvitedDate('');
    setSelectedBuildingsDraft([]);
    setSelectedJobTitleDraft([]);
    setSelectedInvitedDateDraft('');
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setFilter, setPagination, setSortOptions]);

  const updateQueryParam = useCallback(
    (text) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const trimmed = (text || '').trim();
          if (trimmed) {
            next.set('q', trimmed);
            next.set('tab', TREEVIEW_SEARCH_TAB);
          } else {
            next.delete('q');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const runSearch = useCallback(() => {
    const trimmed = (apiSearchText || '').trim();
    if (!trimmed) return;
    resetTableQueryState();
    updateQueryParam(trimmed);
  }, [apiSearchText, resetTableQueryState, updateQueryParam]);

  const clearSearch = useCallback(() => {
    setApiSearchText('');
    resetTableQueryState();
    updateQueryParam('');
  }, [resetTableQueryState, updateQueryParam]);

  const toggleSearchControl = useCallback(() => {
    if (searchActive) {
      clearSearch();
      return;
    }
    runSearch();
  }, [searchActive, clearSearch, runSearch]);

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter') return;
      if (searchActive) {
        clearSearch();
      } else {
        runSearch();
      }
    },
    [searchActive, clearSearch, runSearch],
  );

  const applyFunc = useCallback(() => {
    setSelectedBuildings(selectedBuilingsDraft);
    setSelectedJobTitle(selectedJobTitleDraft);
    setSelectedInvitedDate(selectedInvitedDateDraft);
    setExtraFilters({
      selectedBuilings: selectedBuilingsDraft,
      selectedJobTitle: selectedJobTitleDraft,
      selectedInvitedDate: selectedInvitedDateDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedBuilingsDraft,
    selectedJobTitleDraft,
    selectedInvitedDateDraft,
    setExtraFilters,
    setPagination,
  ]);

  const resetFunc = useCallback(() => {
    setSelectedBuildings([]);
    setSelectedJobTitle([]);
    setSelectedInvitedDate('');
    setSelectedBuildingsDraft([]);
    setSelectedJobTitleDraft([]);
    setSelectedInvitedDateDraft('');
    setExtraFilters(DEFAULT_EXTRA_FILTERS);
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const listFilterChips = useMemo(
    () =>
      buildUserByRoleListFilterChips({
        selectedBuilings,
        selectedJobTitle,
        selectedInvitedDate,
        jobTitleOptions: listOfJobTitle,
      }),
    [selectedBuilings, selectedJobTitle, selectedInvitedDate, listOfJobTitle],
  );

  const handleRemoveListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseUserByRoleListFilterChipId(chipId);
      if (!parsed) return;

      const bumpPage = () =>
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));

      if (parsed.type === 'invitedDate') {
        setSelectedInvitedDate('');
        setSelectedInvitedDateDraft('');
        setExtraFilters((prev) => ({ ...prev, selectedInvitedDate: '' }));
        bumpPage();
        return;
      }
      if (parsed.type === 'propertyCount') {
        const next = selectedBuilings.filter((value) => String(value) !== String(parsed.value));
        setSelectedBuildings(next);
        setSelectedBuildingsDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedBuilings: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'jobTitle') {
        const next = selectedJobTitle.filter((value) => value !== parsed.value);
        setSelectedJobTitle(next);
        setSelectedJobTitleDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedJobTitle: next }));
        bumpPage();
      }
    },
    [selectedBuilings, selectedJobTitle, setExtraFilters, setPagination],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveListFilterChip,
    resetFunc,
  );

  const navigateToUser = useCallback(
    (user, pathSuffix) => {
      const route = resolveUserDelegationRoute(user?.accepter_role);
      const delegationId = user?.id;
      if (!route || !delegationId) {
        dispatch(failedNotif('User detail is not available for this role.'));
        return;
      }
      preloadUserDelegationContext(dispatch, user);
      dispatch(
        openDetails({
          type: 'user',
          data: user,
        }),
      );
      navigate(`/user/${route}/${delegationId}/${pathSuffix}`);
    },
    [dispatch, navigate],
  );

  const onClickUser = useCallback((user) => navigateToUser(user, 'detail'), [navigateToUser]);

  const onClickEdit = useCallback((user) => navigateToUser(user, 'edit'), [navigateToUser]);

  const handleDelete = useCallback(() => {
    if (!selectedUser) return;
    dispatch(
      deleteUsersAction({
        data: {
          requestor_id: selectedUser.requestor_id,
          accepter_id: selectedUser.accepter_id,
          accepter_email: selectedUser.accepter_email,
          requestor_email: selectedUser.requestor_email,
        },
        search: searchActive ? { text: qParam.trim() } : undefined,
      }),
    );
    setSelectedUser(null);
  }, [dispatch, selectedUser, searchActive, qParam]);

  const handleRequestExportData = useCallback(async () => filteredUsers, [filteredUsers]);
  const handleRequestFullExportData = useCallback(async () => usersList, [usersList]);

  const userTableConfig = useMemo(
    () => getUserTableConfigForRoute('treeview', { showUserRole: true }),
    [],
  );

  const resultCountLabel = useMemo(() => {
    if (!searchActive || searchLoading) return null;
    if (usersList.length === 0) return '0 results';
    if (filteredUsers.length === usersList.length) return `${usersList.length} results`;
    return `${filteredUsers.length} of ${usersList.length} results`;
  }, [searchActive, searchLoading, usersList.length, filteredUsers.length]);

  const tableEmptyContent = useMemo(() => {
    if (filter.searchText || dropFilterActiveCount > 0) {
      return (
        <GlobalUXState
          variant="noResults"
          compact
          description="No users match your table search or filters."
        />
      );
    }
    if (searchActive) {
      return (
        <GlobalUXState
          variant="noResults"
          compact
          description="Sorry, there is no matching data to display."
        />
      );
    }
    return (
      <GlobalUXState
        variant="noData"
        compact
        description="Search for users by property name, address, or user name."
      />
    );
  }, [filter.searchText, dropFilterActiveCount, searchActive]);

  return (
    <Box>
      <SearchSection>
        <SearchRow>
          <Input
            value={apiSearchText}
            onChange={(e) => setApiSearchText(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by property or user name..."
            size="medium"
            minWidth="100%"
            fullWidth={false}
            showHelperMessage={false}
            showHelperOnTyping={false}
          />
          <Button
            size="medium"
            variant="icon"
            disabled={!apiSearchText.trim() && !searchActive}
            onClick={toggleSearchControl}
            iconPrefix={searchActive ? 'General.Clear' : 'BookmarkFilter.Search'}
            aria-label={searchActive ? 'Clear search' : 'Search'}
          />
        </SearchRow>

        {resultCountLabel && (
          <Typography variant="body2" style={{ color: colors.neutral[700], marginTop: 8 }}>
            ({resultCountLabel})
          </Typography>
        )}
      </SearchSection>

      <UserTable
        title=""
        users={pagedUsers}
        showUserRole
        enableRowBodyClick
        loading={searchLoading}
        setSelecteduser={setSelectedUser}
        defaultVisibleColumnKeys={userTableConfig.defaultVisibleColumnKeys}
        columnVisibilityStorageKey={userTableConfig.columnVisibilityStorageKey}
        filter={filter}
        onChangeFilter={setFilter}
        dropFilterActiveSummary={dropFilterActiveSummary}
        dropFilterActiveCount={dropFilterActiveCount}
        dropFilter={{
          component: (
            <DropFilterContainer>
              <Dropdown
                onChange={(e) => setSelectedBuildingsDraft(e.target.value)}
                options={numOfBuildings}
                value={selectedBuilingsDraft}
                multiselect
                showSearch={false}
                placeholder=""
                placeholderFlag={false}
                label="Property Count"
                style={{ width: '100%' }}
              />
              <Dropdown
                onChange={(e) => setSelectedJobTitleDraft(e.target.value)}
                options={listOfJobTitle}
                value={selectedJobTitleDraft}
                multiselect
                showSearch={false}
                placeholder=""
                placeholderFlag={false}
                label="Job Title"
                style={{ width: '100%' }}
              />
              <RangeDatePickerContainer>
                <DatePickerInput
                  label="Invited Date"
                  size="small"
                  value={selectedInvitedDateDraft}
                  onChange={(date) => setSelectedInvitedDateDraft(date.target.value)}
                />
              </RangeDatePickerContainer>
            </DropFilterContainer>
          ),
          applyFunc,
          resetFunc,
        }}
        pagination={{
          ...pagination,
          totalItems: filteredUsers.length,
          totalPages: Math.max(1, Math.ceil(filteredUsers.length / pagination.rowsPerPage) || 1),
        }}
        onChangePagination={setPagination}
        sortOptions={sortOptions}
        onChangeSort={setSortOptions}
        onClickUser={onClickUser}
        onClickEdit={onClickEdit}
        onRequestExportData={handleRequestExportData}
        onRequestFullExportData={handleRequestFullExportData}
        exportFullDatasetTotal={usersList.length}
        emptyContent={tableEmptyContent}
      />

      <Dialog open={!!selectedUser}>
        <DialogContainer col1={colors.purple[900]} col2={colors.neutral[900]}>
          <img src={Img_1} alt="" />
          <p>Are you sure you want to delete this user?</p>
          <p>This will remove their access to the Leap portal.</p>
          <div>
            <Button
              size="medium"
              variant="tertiary"
              style={{ flexShrink: 2 }}
              onClick={() => setSelectedUser(null)}
            >
              Discard
            </Button>
            <Button
              size="medium"
              variant="primary"
              style={{ flexShrink: 2 }}
              onClick={handleDelete}
            >
              Yes, Delete
            </Button>
          </div>
        </DialogContainer>
      </Dialog>
    </Box>
  );
};

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 8px;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 12px;
  flex-wrap: nowrap;
  max-width: 480px;

  & > div:first-child {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    align-content: center;
  }

  & > button {
    flex-shrink: 0;
    align-self: center;
    width: auto;
    height: 40px;
    min-height: 40px;
  }
`;

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;

  * {
    box-sizing: border-box;
  }
`;

const RangeDatePickerContainer = styled.div`
  display: flex;
  max-width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;

  * {
    box-sizing: border-box;
  }

  & > div {
    width: 100% !important;
  }

  .ui-kit-input-body {
    width: 278px;
  }
`;

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-radius: 8px;
  width: 350px;
  text-align: center;
  box-sizing: border-box;

  & > p:nth-of-type(1) {
    color: ${(props) => props.col1};
    font-size: 14px;
    font-weight: 600;
  }

  & > p:nth-of-type(2) {
    color: ${(props) => props.col2};
    font-size: 12px;
    font-weight: 400;
  }

  & > div {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    width: 100%;

    button {
      flex: 1;
    }
  }
`;
